import React, { useState, useEffect } from 'react';
import { adminApi, payPeriodsApi, formatDate, formatDateRange } from '../../lib/api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  CheckCircle2, 
  XCircle, 
  Clock,
  Check,
  X,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const AdminTimesheets = () => {
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Custom date range
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  useEffect(() => {
    if (useCustomRange) {
      if (startDate && endDate) {
        fetchEntries();
      }
    } else if (selectedPeriod) {
      fetchEntries();
    }
  }, [selectedPeriod, statusFilter, useCustomRange, startDate, endDate]);

  const fetchPayPeriods = async () => {
    try {
      const response = await payPeriodsApi.getAll();
      setPayPeriods(response.data);
      const current = response.data.find(p => p.is_current);
      if (current) {
        setSelectedPeriod(current.start_date);
      }
    } catch (error) {
      toast.error('Failed to load pay periods');
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const filters = {
        ...(statusFilter !== 'all' && { status: statusFilter })
      };
      
      if (useCustomRange && startDate && endDate) {
        filters.startDate = startDate;
        filters.endDate = endDate;
      } else if (selectedPeriod && selectedPeriod !== 'all') {
        filters.payPeriodStart = selectedPeriod;
      }
      
      const response = await adminApi.getTimeEntries(filters);
      setEntries(response.data);
      setSelectedEntries([]);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (entryId, action) => {
    setActionLoading(true);
    try {
      await adminApi.actionOnEntry(entryId, action);
      toast.success(`Entry ${action}d`);
      fetchEntries();
    } catch (error) {
      toast.error(`Failed to ${action} entry`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }

    setActionLoading(true);
    try {
      for (const entryId of selectedEntries) {
        await adminApi.actionOnEntry(entryId, action);
      }
      toast.success(`${selectedEntries.length} entries ${action}d`);
      fetchEntries();
    } catch (error) {
      toast.error(`Failed to ${action} entries`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleSelectAll = () => {
    const pendingIds = entries.filter(e => e.status === 'pending').map(e => e.id);
    if (selectedEntries.length === pendingIds.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(pendingIds);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
            Pending
          </Badge>
        );
    }
  };

  // Group entries by user
  const groupedEntries = entries.reduce((acc, entry) => {
    if (!acc[entry.user_id]) {
      acc[entry.user_id] = {
        user_name: entry.user_name,
        user_email: entry.user_email,
        entries: []
      };
    }
    acc[entry.user_id].entries.push(entry);
    return acc;
  }, {});

  const pendingEntries = entries.filter(e => e.status === 'pending');

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-timesheets-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Timesheets
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Review and approve contractor time entries
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Toggle between period and custom range */}
            <div className="flex items-center gap-2">
              <Button
                variant={useCustomRange ? "outline" : "default"}
                size="sm"
                onClick={() => setUseCustomRange(false)}
                className={!useCustomRange ? "bg-brick-cyan hover:bg-brick-cyan/90" : "border-slate-300"}
                data-testid="period-mode-btn"
              >
                Pay Period
              </Button>
              <Button
                variant={useCustomRange ? "default" : "outline"}
                size="sm"
                onClick={() => setUseCustomRange(true)}
                className={useCustomRange ? "bg-brick-cyan hover:bg-brick-cyan/90" : "border-slate-300"}
                data-testid="custom-mode-btn"
              >
                <Calendar className="w-4 h-4 mr-1" aria-hidden="true" />
                Custom
              </Button>
            </div>

            {!useCustomRange ? (
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-full sm:w-56 border-slate-300" data-testid="period-filter">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  {payPeriods.map((period) => (
                    <SelectItem key={period.start_date} value={period.start_date}>
                      {formatDateRange(period.start_date, period.end_date)}
                      {period.is_current && ' (Current)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36 border-slate-300"
                  data-testid="start-date-input"
                />
                <span className="text-slate-500 text-sm">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36 border-slate-300"
                  data-testid="end-date-input"
                />
              </div>
            )}

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 border-slate-300" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bulk Actions */}
        {pendingEntries.length > 0 && (
          <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <Checkbox 
              checked={selectedEntries.length === pendingEntries.length && pendingEntries.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-checkbox"
            />
            <span className="text-sm text-slate-600">
              {selectedEntries.length} of {pendingEntries.length} pending selected
            </span>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('approve')}
              disabled={selectedEntries.length === 0 || actionLoading}
              className="font-medium border-green-500 text-green-600 hover:bg-green-50"
              data-testid="bulk-approve-btn"
            >
              <Check className="w-4 h-4 mr-1" aria-hidden="true" />
              Approve Selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('reject')}
              disabled={selectedEntries.length === 0 || actionLoading}
              className="font-medium border-red-500 text-red-600 hover:bg-red-50"
              data-testid="bulk-reject-btn"
            >
              <X className="w-4 h-4 mr-1" aria-hidden="true" />
              Reject Selected
            </Button>
          </div>
        )}

        {/* Entries by User */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-500">Loading...</div>
          </div>
        ) : entries.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="py-12">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" aria-hidden="true" />
                <p className="text-slate-500">
                  No entries found for this period.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedEntries).map(([userId, userData]) => (
              <Card key={userId} className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-200 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg text-slate-900">{userData.user_name}</CardTitle>
                      <p className="font-mono text-sm text-slate-500">{userData.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-brick-cyan" data-testid={`user-${userId}-total-hours`}>
                        {userData.entries.reduce((sum, e) => sum + e.hours, 0).toFixed(1)}
                      </p>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Hours</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {userData.entries.map((entry) => (
                      <div 
                        key={entry.id} 
                        className="flex items-center p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`admin-entry-${entry.id}`}
                      >
                        {entry.status === 'pending' && (
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                            className="mr-4"
                            data-testid={`entry-checkbox-${entry.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-mono text-sm text-brick-cyan">{formatDate(entry.date)}</span>
                            {getStatusBadge(entry.status)}
                          </div>
                          <p className="font-medium text-slate-900 truncate">{entry.project}</p>
                          <p className="text-sm text-slate-500 truncate">{entry.description}</p>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          <div className="text-right">
                            <span className="text-xl font-bold text-slate-900">{entry.hours}</span>
                            <span className="text-sm text-slate-500 ml-1">hrs</span>
                          </div>
                          {entry.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAction(entry.id, 'approve')}
                                disabled={actionLoading}
                                className="hover:bg-green-50"
                                data-testid={`approve-entry-${entry.id}`}
                              >
                                <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAction(entry.id, 'reject')}
                                disabled={actionLoading}
                                className="hover:bg-red-50"
                                data-testid={`reject-entry-${entry.id}`}
                              >
                                <X className="w-4 h-4 text-red-600" aria-hidden="true" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTimesheets;
